/**
 * Sessions (P28 + P30 strip).
 *
 * V1 has no session-CRUD API — sessions live in the proxy process memory
 * (a transport-layer concept, M-WIRE §13). The wireframe SessionBuilder /
 * CodeSample / SessionCards widgets were demos with no backend; removed.
 *
 * What's left is the empty-state for the live sessions table, which the
 * /api/v1/sessions stub returns. When we add a cross-process broadcast
 * (V2), this page lights up.
 */
import type { JSX } from 'react';
import { TimerReset } from 'lucide-react';

import { useSessions } from '@/api/index.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell, EmptyState } from '@/components/console/StateBlocks.js';

export default function Sessions(): JSX.Element {
  const sessions = useSessions();
  const rows = (sessions.data?.items ?? []) as readonly DataRow[];

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title="Sessions"
        description="Active proxy sessions across the gateway. Each session binds a client + auth context to a stream of MCP calls."
      />

      <SectionCard
        icon={TimerReset}
        title="Active sessions"
        text="Sessions are immutable; create a new one when toolkits, account, or policy contract changes."
      >
        <AsyncShell
          isLoading={sessions.isLoading}
          isError={sessions.isError}
          isEmpty={rows.length === 0}
          error={sessions.error}
          loadingTitle="Loading sessions"
          errorTitle="Sessions API unreachable"
          emptyTitle="No live session data"
          emptyHint={
            <>
              {sessions.data?.detail ??
                'Sessions live in proxy process memory; an aggregate broadcast endpoint is a future enhancement.'}
            </>
          }
        >
          <DataTable
            columns={['id', 'user', 'toolkits', 'client', 'mode', 'state', 'tools', 'created']}
            rows={rows}
          />
        </AsyncShell>
      </SectionCard>

      {/* Always show the empty-state hint — useful guidance even when API is up */}
      {rows.length === 0 && !sessions.isLoading && !sessions.isError && (
        <EmptyState
          title="No active sessions"
          hint={
            <>
              When a client (Claude Desktop, etc.) connects to the gateway and runs{' '}
              <code>initialize</code>, the session appears here once proxy → control-plane sync
              lands in a future release.
            </>
          }
          compact
        />
      )}
    </>
  );
}
